import { queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID, RECEIVE_DATA_OBJECTS, STORE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import { meetingsManager } from '@inpart-io/padme-iap';
import {
    type IMergeAsyncExecStatusResult,
    type IPrivateCompany,
    type IPrivateContact,
    privateCompaniesManager,
    privateCompanyProperties
} from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { getDobjId } from '@/services/data-object-helper';

import { PRIVATE_CONTACT_MODULE } from '../private-contact-module';
import { SET_MATCHES } from './mutations';

export const UPDATE_LINK = 'updateLink';
export const CREATE_COMPANY = 'CREATE_COMPANY';
export const COUNT_MEETINGS = 'countMeetings';
export const INCREMENT_MEETINGS = 'incrementMeetings';
export const DECREMENT_MEETINGS = 'decrementMeetings';
export const MERGE = 'merge';
export const RETRIEVE_MATCHES = 'retrieveMatches';
export const OVERRIDE_EVALUATE = 'overrideEvaluate';

export const actions: ActionTree<IPrivateCompany, unknown> = {
    async [UPDATE_LINK](context, { companyId, directoryId, properties }: { companyId: number; directoryId?: number; properties?: string }): Promise<void> {
        const updated = await privateCompaniesManager.updateLink(companyId, directoryId, { properties });
        return context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [updated] });
    },
    async [CREATE_COMPANY](context, { companyList, properties }: { companyList: IPrivateCompany[]; properties?: string }): Promise<IPrivateCompany[]> {
        const created = await privateCompaniesManager.createCompany(companyList, { properties });
        await context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: created });
        return created;
    },
    async [COUNT_MEETINGS](context, { companyId }: { companyId: number }): Promise<void> {
        const query = queryBuilder.build(queryBuilder.op.or(queryBuilder.cmp.equalTo('company', companyId)));
        const meetingsNb = await meetingsManager.count(query);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [{ dobjId: companyId, meetingsNb }] });
    },
    [INCREMENT_MEETINGS](context, { companyId }: { companyId: number }): void {
        const company = context.getters[GET_BY_ID](companyId);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [{ dobjId: companyId, meetingsNb: ((company.meetingsNb as number) ?? 0) + 1 }] });
    },
    [DECREMENT_MEETINGS](context, { companyId }: { companyId: number }): void {
        const company = context.getters[GET_BY_ID](companyId);
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [{ dobjId: companyId, meetingsNb: ((company.meetingsNb as number) ?? 0) - 1 }] });
    },
    async [MERGE](
        context,
        { target, toMerge, properties }: { target: IPrivateCompany; toMerge: IPrivateCompany; properties: string }
    ): Promise<IMergeAsyncExecStatusResult> {
        const countryId = (target.address?.country ?? null) !== null ? getDobjId(target.address.country) : getDobjId(toMerge.address?.country);

        const dataObject: IPrivateCompany = {
            dobjId: target.dobjId,
            companyTypes: target.companyTypes?.length
                ? target.companyTypes.map((val) => {
                      return getDobjId(val);
                  })
                : toMerge.companyTypes?.map((val) => {
                      return getDobjId(val);
                  }),
            urls: target.urls?.length ? target.urls : toMerge.urls,
            address: {
                country: countryId,
                zip: target.address?.zip ?? toMerge.address?.zip,
                state: target.address?.state ?? toMerge.address?.state,
                address1: target.address?.address1 ?? toMerge.address?.address1,
                city: target.address?.city ?? toMerge.address?.city
            },
            description: target.description?.length && target.description !== '<p></p>' ? target.description : toMerge.description
        };
        const statusId: string = await privateCompaniesManager.merge(dataObject, toMerge.dobjId, properties);
        return privateCompaniesManager.getExecutionManager().waitForStatus(statusId);
    },
    async [OVERRIDE_EVALUATE](
        context,
        { companyId, evaluateId, oldId, properties }: { companyId: number; evaluateId: number; oldId?: number; properties?: string }
    ): Promise<void> {
        const updated = await privateCompaniesManager.overrideEvaluate(companyId, evaluateId, oldId, { properties });
        return context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [updated] });
    },
    async [RETRIEVE_MATCHES](context, { companyId }: { companyId: number }): Promise<void> {
        const company: IPrivateCompany = context.getters[GET_BY_ID](companyId);
        // we retrieve the matches search result and extract only the matches that are not the original object
        const matches = (await privateCompaniesManager.retrieveMatches(company.name, privateCompanyProperties.DEFAULT))
            ?.map((el) => {
                return el.match;
            })
            .filter((match) => {
                return match?.dobjId !== companyId;
            });
        context.commit(SET_MATCHES, {
            companyId,
            matches
        });
    },
    [STORE_DATA_OBJECTS](context, { dataObjectList }: { dataObjectList: IPrivateCompany[] }) {
        const companies: IPrivateCompany[] = [];
        const contacts = new Map<number, IPrivateContact>();

        dataObjectList.forEach((company) => {
            const companyContacts = company['@(:PrivateContactCompany:company)'] ?? [];

            if (companyContacts.length) {
                companyContacts.forEach((companyContact) => {
                    const relatedContact = companyContact.contact as IPrivateContact;
                    contacts.set(relatedContact.dobjId, companyContact.contact as IPrivateContact);
                });
            }

            companies.push(company);
        });

        const contactsValue = Array.from(contacts.values());

        if (companies.length > 0) {
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: companies });
        }

        if (contactsValue.length > 0) {
            void context.dispatch(`${PRIVATE_CONTACT_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: contactsValue }, { root: true });
        }
    }
};
